import { useEffect, useState, useCallback } from 'react';
import { contenidoPublicoService } from '../services/contenido-publico/contenido-publico.service';
import type { PaginaPublica, ContenidoPublicoGrouped, ContenidoPublico } from '../types/contenido-publico';

export function usePublicContent(pagina: PaginaPublica) {
  const [content, setContent] = useState<ContenidoPublicoGrouped>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContent = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await contenidoPublicoService.findByPagina(pagina);
      setContent(data);
    } catch (err: any) {
      console.error(`Error cargando contenido de ${pagina}:`, err);
      setError(err.response?.data?.message || 'Error cargando contenido');
    } finally {
      setLoading(false);
    }
  }, [pagina]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const getValue = useCallback(
    (seccion: string, clave: string, fallback = ''): string => {
      const items = content[seccion];
      if (!items) return fallback;
      const item = items.find((i) => i.clave === clave && i.activo);
      return item ? item.valor : fallback;
    },
    [content],
  );

  const getText = useCallback(
    (seccion: string, clave: string, fallback = ''): string => getValue(seccion, clave, fallback),
    [getValue],
  );

  const getImage = useCallback(
    (seccion: string, clave: string, fallback?: string): string | undefined => {
      const url = getValue(seccion, clave);
      if (!url) return fallback;
      if (url.startsWith('http')) return url;
      return `${import.meta.env.VITE_API_BASE_URL?.replace('/api/v1', '') || 'http://localhost:3000'}${url}`;
    },
    [getValue],
  );

  const getJson = useCallback(
    <T = any>(seccion: string, clave: string, fallback: T): T => {
      const items = content[seccion];
      if (!items) return fallback;
      const item = items.find((i) => i.clave === clave && i.activo);
      if (!item) return fallback;
      try {
        return JSON.parse(item.valor) as T;
      } catch {
        return item.metadata as T ?? fallback;
      }
    },
    [content],
  );

  const getItems = useCallback(
    (seccion: string): ContenidoPublico[] => {
      return content[seccion]?.filter((i) => i.activo) ?? [];
    },
    [content],
  );

  return {
    content,
    loading,
    error,
    refetch: fetchContent,
    getValue,
    getText,
    getImage,
    getJson,
    getItems,
  };
}
